

import { useEffect, useState } from "react";
import { Flag, CheckCircle2, XCircle } from "lucide-react";
import { axiosInstance } from "../../../utility/apiInstance";
import { FlagsPropsType, ShipmentFlagArray } from "./types/ActionTypes";
import toast from "react-hot-toast";

const s: Record<string, React.CSSProperties> = {
  root: { padding: "14px 0 0" },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  sectionTitle: { display: "flex", alignItems: "center", gap: "7px" },
  sectionAccent: { width: "3px", height: "16px", background: "#F8B71F", borderRadius: "99px" },
  sectionLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  rowCount: { fontSize: "11px", color: "#9ca3af", fontWeight: 500 },

  colHeaders: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr 1fr 1fr",
    padding: "0 12px",
    marginBottom: "6px",
    minWidth: "700px",
  },
  colHeader: {
    fontSize: "10.5px",
    fontWeight: 600,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    padding: "0 5px",
  },

  rowWrap: { position: "relative", marginBottom: "6px" },
  rowCard: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr 1fr 1fr",
    background: "#fff",
    border: "1px solid #efefef",
    borderRadius: "10px",
    padding: "10px 12px",
    alignItems: "center",
    minWidth: "700px",
    transition: "box-shadow 0.15s, border-color 0.15s",
  },
  rowCardHovered: { borderColor: "#e0e0e0", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" },
  cell: { padding: "0 5px" },

  flagName: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    fontWeight: 600,
    color: "#dc2626",
  },
  text: { fontSize: "12px", color: "#374151" },
  textNa: { fontSize: "12px", color: "#9ca3af" },

  statusYes: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    background: "#dcfce7",
    color: "#166534",
    fontSize: "11px",
    fontWeight: 600,
    padding: "3px 8px",
    borderRadius: "999px",
    width: "fit-content",
  },
  statusNo: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    background: "#fee2e2",
    color: "#991b1b",
    fontSize: "11px",
    fontWeight: 600,
    padding: "3px 8px",
    borderRadius: "999px",
    width: "fit-content",
  },

  emptyWrap: {
    border: "1px dashed #d1d5db",
    borderRadius: "10px",
    minHeight: "110px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
    fontSize: "12px",
    background: "#fafafa",
  },
};

export default function TabFlags({ trackingNumber,uniqueValue }: FlagsPropsType) {
  const [shipmentFlags, setShipmentFlags] = useState<ShipmentFlagArray>([]);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  async function shipmentFlag() {
    try {
      const { data } = await axiosInstance.get(
        `/api/v1/shipment-flag?awbNumber=${trackingNumber}&uniqueValue=${uniqueValue}`
      );
      setShipmentFlags(data.data);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message;
      toast.error(errorMessage);
    }
  }

  useEffect(() => {
    shipmentFlag();
  }, []);

  const COLS = ["Flag", "Impacted Leg", "Date Reported", "Action Taken"];

  return (
    <div style={s.root}>
      <div style={s.sectionHeader}>
        <div style={s.sectionTitle}>
          <div style={s.sectionAccent} />
          <span style={s.sectionLabel}>Shipment Flags</span>
        </div>
        <span style={s.rowCount}>
          {shipmentFlags.length} flag{shipmentFlags.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <div style={s.colHeaders}>
          {COLS.map((h) => (
            <span key={h} style={s.colHeader}>
              {h}
            </span>
          ))}
        </div>

        {shipmentFlags.length > 0 ? (
          shipmentFlags.map((item: any) => {
            const isHovered = hoveredRow === item._id;
            return (
              <div
                key={item._id}
                style={s.rowWrap}
                onMouseEnter={() => setHoveredRow(item._id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <div style={{ ...s.rowCard, ...(isHovered ? s.rowCardHovered : {}) }}>
                  <div style={s.cell}>
                    <span style={s.flagName}>
                      <Flag size={13} />
                      {item?.flagName || "N/A"}
                    </span>
                  </div>

                  <div style={s.cell}>
                    <span style={item.leg ? s.text : s.textNa}>{item.leg || "N/A"}</span>
                  </div>

                  <div style={s.cell}>
                    <span style={s.text}>
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>

                  <div style={s.cell}>
                    {item.isActionTaken ? (
                      <span style={s.statusYes}>
                        <CheckCircle2 size={12} />
                        Yes
                      </span>
                    ) : (
                      <span style={s.statusNo}>
                        <XCircle size={12} />
                        No
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div style={s.emptyWrap}>No flags found.</div>
        )}
      </div>
    </div>
  );
}